import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import { web3Service } from '@/services/web3';
import { Drop, Stream, NFT, Tip } from '@/types';
import { FiPackage, FiVideo, FiImage, FiDollarSign, FiPlus, FiExternalLink } from 'react-icons/fi';
import { formatCurrency } from '@/utils/formatters';

const DashboardPage = () => {
  const { user } = useAuthStore();
  const [fttfBalance, setFttfBalance] = useState<string>('0');
  const [dusdBalance, setDusdBalance] = useState<string>('0');

  // Fetch user's wallet balances
  useEffect(() => {
    const fetchBalances = async () => {
      if (user) {
        try {
          const fttf = await web3Service.getFTTFBalance(user.walletAddress);
          const dusd = await web3Service.getDUSDBalance(user.walletAddress);
          
          setFttfBalance(fttf);
          setDusdBalance(dusd);
        } catch (error) {
          console.error('Error fetching balances:', error);
        }
      }
    };
    
    fetchBalances();
  }, [user]);

  // Fetch user's drops
  const { data: drops, isLoading: isLoadingDrops } = useQuery({
    queryKey: ['userDrops', user?.id],
    queryFn: async () => {
      const response = await api.get<Drop[]>(`/api/drops/creator/${user?.id}`);
      return response.data.data || [];
    },
    enabled: !!user
  });

  // Fetch user's streams
  const { data: streams, isLoading: isLoadingStreams } = useQuery({
    queryKey: ['userStreams', user?.id],
    queryFn: async () => {
      const response = await api.get<Stream[]>(`/api/streams/creator/${user?.id}`);
      return response.data.data || [];
    },
    enabled: !!user
  });

  // Fetch user's NFTs
  const { data: nfts, isLoading: isLoadingNfts } = useQuery({
    queryKey: ['userNfts', user?.id],
    queryFn: async () => {
      const response = await api.get<NFT[]>(`/api/nfts/creator/${user?.id}`);
      return response.data.data || [];
    },
    enabled: !!user
  });

  // Fetch tips received
  const { data: tips, isLoading: isLoadingTips } = useQuery({
    queryKey: ['userTips', user?.id],
    queryFn: async () => {
      const response = await api.get<Tip[]>(`/api/tipping/received/${user?.id}`);
      return response.data.data || [];
    },
    enabled: !!user
  });

  // Calculate total tips received
  const totalTips = Array.isArray(tips) ? tips.reduce((sum: number, tip: any) => sum + parseFloat(tip.amount), 0) : 0;

  return (
    <div>
      <h1 className="text-3xl font-bold text-white mb-8">Creator Dashboard</h1>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Drops Stats */}
        <div className="bg-dark-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Drops</h3>
            <FiPackage className="text-primary-500 text-xl" />
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            {isLoadingDrops ? '...' : (Array.isArray(drops) ? drops.length : 0)}
          </p>
          <div className="flex justify-between items-center">
            <p className="text-gray-400">Total Drops</p>
            <Link
              to="/dashboard/drops"
              className="text-primary-500 hover:text-primary-400 text-sm flex items-center"
            >
              View All <FiExternalLink className="ml-1" />
            </Link>
          </div>
        </div>

        {/* Streams Stats */}
        <div className="bg-dark-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Streams</h3>
            <FiVideo className="text-secondary-500 text-xl" />
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            {isLoadingStreams ? '...' : (Array.isArray(streams) ? streams.length : 0)}
          </p>
          <div className="flex justify-between items-center">
            <p className="text-gray-400">Total Streams</p>
            <Link
              to="/dashboard/streams"
              className="text-secondary-500 hover:text-secondary-400 text-sm flex items-center"
            >
              View All <FiExternalLink className="ml-1" />
            </Link>
          </div>
        </div>

        {/* NFTs Stats */}
        <div className="bg-dark-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">NFTs</h3>
            <FiImage className="text-primary-500 text-xl" />
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            {isLoadingNfts ? '...' : (Array.isArray(nfts) ? nfts.length : 0)}
          </p>
          <div className="flex justify-between items-center">
            <p className="text-gray-400">Total Minted</p>
            <Link
              to="/dashboard/nfts"
              className="text-primary-500 hover:text-primary-400 text-sm flex items-center"
            >
              View All <FiExternalLink className="ml-1" />
            </Link>
          </div>
        </div>

        {/* Tips Stats */}
        <div className="bg-dark-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Tips</h3>
            <FiDollarSign className="text-secondary-500 text-xl" />
          </div>
          <p className="text-3xl font-bold text-white mb-2">
            {isLoadingTips ? '...' : formatCurrency(totalTips, 'USD')}
          </p>
          <div className="flex justify-between items-center">
            <p className="text-gray-400">Total Received</p>
            <Link
              to="/dashboard/wallet"
              className="text-secondary-500 hover:text-secondary-400 text-sm flex items-center"
            >
              View Details <FiExternalLink className="ml-1" />
            </Link>
          </div>
        </div>
      </div>

      {/* Wallet Overview */}
      <div className="bg-dark-800 rounded-xl p-6 mb-8">
        <h2 className="text-xl font-bold text-white mb-4">Wallet Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-dark-700 rounded-lg p-4">
            <p className="text-gray-400 mb-2">FTTF Balance</p>
            <p className="text-2xl font-bold text-white">{fttfBalance} FTTF</p>
          </div>
          <div className="bg-dark-700 rounded-lg p-4">
            <p className="text-gray-400 mb-2">dUSD Balance</p>
            <p className="text-2xl font-bold text-white">{dusdBalance} dUSD</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-dark-800 rounded-xl p-6 mb-8">
        <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Link
            to="/dashboard/drops/create"
            className="bg-dark-700 hover:bg-dark-600 rounded-lg p-4 flex items-center transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center mr-3">
              <FiPlus className="text-white" />
            </div>
            <span className="text-white">Create Drop</span>
          </Link>
          <Link
            to="/dashboard/streams/create"
            className="bg-dark-700 hover:bg-dark-600 rounded-lg p-4 flex items-center transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-secondary-600 flex items-center justify-center mr-3">
              <FiPlus className="text-white" />
            </div>
            <span className="text-white">Start Stream</span>
          </Link>
          <Link
            to="/dashboard/nfts/mint"
            className="bg-dark-700 hover:bg-dark-600 rounded-lg p-4 flex items-center transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center mr-3">
              <FiPlus className="text-white" />
            </div>
            <span className="text-white">Mint NFT</span>
          </Link>
          <Link
            to="/marketplace"
            className="bg-dark-700 hover:bg-dark-600 rounded-lg p-4 flex items-center transition-colors"
          >
            <div className="w-10 h-10 rounded-full bg-secondary-600 flex items-center justify-center mr-3">
              <FiExternalLink className="text-white" />
            </div>
            <span className="text-white">Visit Marketplace</span>
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-dark-800 rounded-xl p-6">
        <h2 className="text-xl font-bold text-white mb-4">Recent Activity</h2>
        {/* Activity list would go here */}
        <p className="text-gray-400 text-center py-8">No recent activity to display</p>
      </div>
    </div>
  );
};

export default DashboardPage;